import type { Epic } from '../../index.ts'
import {
  cubePrefetchRequested,
  cubeStateKey,
  visualizationSlice
} from '../../Slice/Visualization/VisualizationSlice.ts'
import { BlastSource } from '../../../Model/Project/BlastSource.ts'
import { EMPTY, Observable, filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs'

/**
 * Background-prefetch the *sibling* blast types once the foreground cube has loaded, so that
 * switching blast type in the workspace is instant on typical projects.
 *
 * The foreground request is emitted by `cubeAutoRequestEpic`; when it resolves we observe a
 * `cubeReady`. From there:
 *   - only act on the currently-active scope (a stale `cubeReady` from a scope the user already
 *     navigated away from must not trigger fetches),
 *   - gate on project size — above [PREFETCH_SAMPLE_THRESHOLD] samples a cube is too heavy to
 *     speculatively fetch, so we lazy-load on demand instead; if the size is unknown we err on
 *     the side of NOT prefetching,
 *   - skip any blast type already present in the `cubes` slice (requested, ready, or errored),
 *   - emit `cubePrefetchRequested` on idle (`requestIdleCallback`, falling back to a macrotask)
 *     so the speculative work never competes with the initial paint.
 *
 * Crucially we emit `cubePrefetchRequested`, NOT `cubeRequested`: prefetch warms the IndexedDB
 * byte cache only (`CubePrefetchLoaderEpic`) and does NOT register a decoded table in the
 * `CubeTableRegistry`. Registering would let the speculative siblings evict the foreground table
 * (the registry is bounded at `MAX_RESIDENT_TABLES`), which would strand the visible sink on a
 * permanent spinner. Switching blast type later still hits the warm cache → 304 → decode-from-
 * cache, so it stays network-free without holding every blast type resident in memory.
 *
 */
export const PREFETCH_SAMPLE_THRESHOLD = 1000

export const cubePrefetchEpic: Epic = (action$, state$, { [visualizationSlice.name]: visualization }) =>
  action$.pipe(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mergeMap((a) => (visualization.cubeReady.match(a) ? [a as any] : [])),
    withLatestFrom(state$),
    mergeMap(([action, s]) => {
      const key: string = action.payload.key
      const sep = key.lastIndexOf(':')
      const scopeId = key.slice(0, sep)
      const readyBlast = key.slice(sep + 1)

      // Only prefetch for the scope the workspace is currently showing.
      if (!scopeId || scopeId !== s.visualization.scope?.id) return EMPTY

      const prefetch = (st: typeof s) => {
        const nSamples = maxKnownSampleCount(st.visualization.sources)
        if (nSamples === null || nSamples >= PREFETCH_SAMPLE_THRESHOLD) return EMPTY

        const siblings = Object.values(BlastSource).filter(
          (bt) => bt !== readyBlast && !st.visualization.cubes[cubeStateKey(scopeId, bt)]
        )
        if (siblings.length === 0) return EMPTY

        return emitOnIdle(siblings).pipe(
          // A left scope's prefetches would spend the user's cube rate limit the new scope's cube needs.
          takeUntil(action$.pipe(filter(visualization.setScope.match))),
          map((bt) => cubePrefetchRequested({ scopeId, blastType: bt }))
        )
      }

      if (maxKnownSampleCount(s.visualization.sources) !== null) return prefetch(s)
      // The workspace requests its default cube on entry, so it can be ready before any source has
      // counted the samples: decide on the first count instead.
      return action$.pipe(
        filter(visualization.bucketsReady.match),
        take(1),
        withLatestFrom(state$),
        takeUntil(action$.pipe(filter(visualization.setScope.match))),
        mergeMap(([, st]) => prefetch(st))
      )
    })
  )

/**
 * Largest known `meta.nSamples` across sources. The slice holds a single active scope, so every
 * source belongs to it. Returns null when no source has reported a sample count yet — the caller
 * treats "unknown" as "don't prefetch".
 */
function maxKnownSampleCount(
  sources: Record<string, { snapshot: { meta: { nSamples?: number } } | null } | undefined>
): number | null {
  let max: number | null = null
  for (const src of Object.values(sources)) {
    const n = src?.snapshot?.meta?.nSamples
    if (typeof n === 'number') max = max === null ? n : Math.max(max, n)
  }
  return max
}

/**
 * Emit each item from the browser's idle queue (one `requestIdleCallback` per item), completing
 * after the last fires. Falls back to a macrotask where `requestIdleCallback` is unavailable
 * (Safari, non-DOM test envs). Cancels all pending callbacks on unsubscribe.
 */
function emitOnIdle<T>(items: T[]): Observable<T> {
  return new Observable<T>((subscriber) => {
    let remaining = items.length
    const cancels: Array<() => void> = []
    const hasRIC = typeof requestIdleCallback === 'function'

    for (const item of items) {
      const fire = () => {
        subscriber.next(item)
        if (--remaining === 0) subscriber.complete()
      }
      if (hasRIC) {
        const id = requestIdleCallback(fire, { timeout: 5000 })
        cancels.push(() => cancelIdleCallback(id))
      } else {
        const id = setTimeout(fire, 0)
        cancels.push(() => clearTimeout(id))
      }
    }

    return () => cancels.forEach((cancel) => cancel())
  })
}
